import type { DropdownSelectItem } from './dropdown-select.types'

export interface ListedDropdownSelectItem {
  item: DropdownSelectItem
  depth: number
}

/** Lists every item with how deep it sits, each parent right before its children. */
export const listItems = (items: DropdownSelectItem[], depth = 0): ListedDropdownSelectItem[] =>
  items.flatMap((item) => [{ item, depth }, ...listItems(item.children ?? [], depth + 1)])

export interface ItemTree {
  childIdsById: Map<string, string[]>
  parentIdById: Map<string, string>
}

export const indexItemTree = (items: DropdownSelectItem[]): ItemTree => {
  const tree: ItemTree = { childIdsById: new Map(), parentIdById: new Map() }
  const index = (item: DropdownSelectItem) => {
    if (!item.children?.length) return
    tree.childIdsById.set(
      item.id,
      item.children.map((child) => child.id),
    )
    for (const child of item.children) {
      tree.parentIdById.set(child.id, item.id)
      index(child)
    }
  }
  items.forEach(index)
  return tree
}

const descendantIds = (tree: ItemTree, id: string): string[] =>
  (tree.childIdsById.get(id) ?? []).flatMap((childId) => [childId, ...descendantIds(tree, childId)])

const ancestorIds = (tree: ItemTree, id: string): string[] => {
  const parentId = tree.parentIdById.get(id)
  return parentId ? [parentId, ...ancestorIds(tree, parentId)] : []
}

const addIds = (ids: string[], added: string[]): string[] => [
  ...ids,
  ...added.filter((id) => !ids.includes(id)),
]

const removeIds = (ids: string[], removed: string[]): string[] =>
  ids.filter((id) => !removed.includes(id))

/** Checks or unchecks the item with everything under it. Each item above it is then checked exactly
 * when all its children are. */
export const toggleItem = (
  selectedIds: string[],
  itemId: string,
  checked: boolean,
  tree: ItemTree,
): string[] => {
  const toggledIds = [itemId, ...descendantIds(tree, itemId)]
  let nextIds = checked ? addIds(selectedIds, toggledIds) : removeIds(selectedIds, toggledIds)
  for (const ancestorId of ancestorIds(tree, itemId)) {
    const childIds = tree.childIdsById.get(ancestorId) ?? []
    nextIds = childIds.every((id) => nextIds.includes(id))
      ? addIds(nextIds, [ancestorId])
      : removeIds(nextIds, [ancestorId])
  }
  return nextIds
}

/** The unchecked items with something checked under them, shown as [-]. */
export const getPartiallySelectedIds = (selectedIds: string[], tree: ItemTree): Set<string> => {
  const selected = new Set(selectedIds)
  return new Set(
    [...tree.childIdsById.keys()].filter(
      (id) =>
        !selected.has(id) && descendantIds(tree, id).some((descendant) => selected.has(descendant)),
    ),
  )
}
